export const RESPONSE_CHANNELS = ["whatsapp", "email", "both"];

export function normalizeWhatsappNumber(value = "", defaultCountryCode = "27") {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (raw.startsWith("+")) return `+${digits}`;
  if (digits.startsWith(defaultCountryCode)) return `+${digits}`;
  if (digits.startsWith("0")) return `+${defaultCountryCode}${digits.slice(1)}`;
  return `+${digits}`;
}

export function normalizeResponseIdentity(person = {}) {
  const nested = person.responseIdentity || {};
  const preferred = RESPONSE_CHANNELS.includes(nested.preferredChannel)
    ? nested.preferredChannel
    : RESPONSE_CHANNELS.includes(person.preferredResponseChannel)
      ? person.preferredResponseChannel
      : "whatsapp";

  return {
    whatsappNumber: normalizeWhatsappNumber(
      nested.whatsappNumber || person.whatsappNumber || person.mobile || person.phone || ""
    ),
    email: String(nested.email || person.email || "").trim().toLowerCase(),
    preferredChannel: preferred,
    whatsappVerified: nested.whatsappVerified === true,
    emailVerified: nested.emailVerified === true,
    whatsappVerifiedAt: nested.whatsappVerifiedAt || null,
    emailVerifiedAt: nested.emailVerifiedAt || null,
    lastWhatsappTest: nested.lastWhatsappTest || null,
  };
}

export function buildResponseIdentity({
  whatsappNumber = "",
  email = "",
  preferredChannel = "whatsapp",
  previous = {},
} = {}) {
  const prior = normalizeResponseIdentity(previous);
  const normalizedWhatsapp = normalizeWhatsappNumber(whatsappNumber);
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const channel = RESPONSE_CHANNELS.includes(preferredChannel) ? preferredChannel : "whatsapp";
  const sameWhatsapp = Boolean(normalizedWhatsapp) && normalizedWhatsapp === prior.whatsappNumber;
  const sameEmail = Boolean(normalizedEmail) && normalizedEmail === prior.email;

  return {
    whatsappNumber: normalizedWhatsapp,
    email: normalizedEmail,
    preferredChannel: channel,
    whatsappVerified: sameWhatsapp ? prior.whatsappVerified : false,
    emailVerified: sameEmail ? prior.emailVerified : false,
    whatsappVerifiedAt: sameWhatsapp && prior.whatsappVerified ? (prior.whatsappVerifiedAt || null) : null,
    emailVerifiedAt: sameEmail && prior.emailVerified ? (prior.emailVerifiedAt || null) : null,
    lastWhatsappTest: sameWhatsapp ? (prior.lastWhatsappTest || null) : null,
  };
}

export function buildResponseProfile(person = {}, { personId = "", personType = "staff" } = {}) {
  const identity = normalizeResponseIdentity(person);
  const archived = person?.archived === true || person?.isArchived === true;
  const whatsappConfigured = /^\+[1-9]\d{7,14}$/.test(identity.whatsappNumber);
  const emailConfigured = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.email);
  const preferredConfigured = identity.preferredChannel === "both"
    ? whatsappConfigured && emailConfigured
    : identity.preferredChannel === "email"
      ? emailConfigured
      : whatsappConfigured;
  const preferredVerified = identity.preferredChannel === "both"
    ? identity.whatsappVerified && identity.emailVerified
    : identity.preferredChannel === "email"
      ? identity.emailVerified
      : identity.whatsappVerified;

  return {
    personId: personId || person?.id || "",
    personType,
    name: String(person?.name || "").trim(),
    storeId: String(person?.storeId || "").trim(),
    roleId: String(person?.roleId || "").trim(),
    active: !archived,
    preferredChannel: identity.preferredChannel,
    channels: {
      whatsapp: {
        address: identity.whatsappNumber,
        configured: whatsappConfigured,
        verified: identity.whatsappVerified,
        eligible: !archived && whatsappConfigured,
      },
      email: {
        address: identity.email,
        configured: emailConfigured,
        verified: identity.emailVerified,
        eligible: !archived && emailConfigured,
      },
    },
    readiness: archived
      ? "inactive"
      : !preferredConfigured
        ? "incomplete"
        : preferredVerified
          ? "ready"
          : "verification_required",
  };
}

export function responseIdentityStatus(identity = {}) {
  return buildResponseProfile({ responseIdentity: identity }).readiness;
}

export function responseIdentityLabel(person = {}) {
  const profile = buildResponseProfile(person, { personId: person?.id || "" });
  if (profile.readiness === "inactive") return "Inactive";
  if (profile.readiness === "incomplete") return "Identity needed";
  if (profile.readiness === "verification_required") return "Verification needed";
  return "Response ready";
}
